import React from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  WarningAmber as WarningIcon,
  SentimentVeryDissatisfied as SadIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Event as EventIcon,
  PhoneAndroid as DeviceIcon,
  SupportAgent as AgentIcon,
  Call as CallIcon,
  WhatsApp as WhatsAppIcon,
  Mail as MailIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { useSelector, useDispatch } from 'react-redux';
import {
  storeAlertRiskLevelTriggerLogFollowUp,
  getAlertRiskLevelTriggerLogStore,
  updateAlertRiskLevelTriggerLogStore,
} from '../../../../store/ratings/thunks';

interface PanelSeguimientoCasoProps {
  data: any;
  onClose: () => void;
}

export const PanelSeguimientoCaso: React.FC<PanelSeguimientoCasoProps> = ({ data, onClose }) => {
  const dispatch = useDispatch();
  const { alert_risk_level_trigger_log } = useSelector((state: any) => state.ratingsDevices);
  const auth = useSelector((state: any) => state.auth);

  const [formFollowUp, setFormFollowUp] = React.useState({
    contactMethod: 'whatsapp',
    result: 'Cliente respondió',
    observations: '',
  });

  const handleSaveFollowUp = async () => {
    if (!formFollowUp.observations) return;

    const payload = {
      contactMethod: formFollowUp.contactMethod,
      result: formFollowUp.result,
      observations: formFollowUp.observations,
    };

    const result = await dispatch(
      storeAlertRiskLevelTriggerLogFollowUp(alert_risk_level_trigger_log.id, payload)
    );
    if (result && result.ok) {
      setFormFollowUp({ ...formFollowUp, observations: '' });
      dispatch(getAlertRiskLevelTriggerLogStore(alert_risk_level_trigger_log.id));
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    const payload = { managementStatus: newStatus };
    const result = await dispatch(
      updateAlertRiskLevelTriggerLogStore(alert_risk_level_trigger_log.id, payload)
    );
    if (result && result.ok) {
      dispatch(getAlertRiskLevelTriggerLogStore(alert_risk_level_trigger_log.id));
    }
  };

  if (!alert_risk_level_trigger_log) {
    return (
      <Box
        id="panel-seguimiento-caso"
        sx={{
          mt: 4,
          bgcolor: '#ffffff',
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          border: '1px solid #E0E0E0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const caseData = alert_risk_level_trigger_log;

  return (
    <Box
      id="panel-seguimiento-caso"
      sx={{
        mt: 4,
        bgcolor: '#ffffff',
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        border: '1px solid #E0E0E0',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{
            color: '#4A148C',
            fontWeight: 800,
            textTransform: 'uppercase',
            mb: 2,
            fontSize: '0.9rem',
          }}
        >
          3. PANEL DE SEGUIMIENTO DEL CASO
        </Typography>

        {/* Header */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            startIcon={<ArrowBackIcon sx={{ fontSize: '1rem' }} />}
            onClick={onClose}
            sx={{
              alignSelf: 'flex-start',
              textTransform: 'none',
              color: '#4A148C',
              fontWeight: 700,
              p: 0,
              '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
            }}
          >
            Volver a encuestas
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#2e263d' }}>
              Seguimiento de Caso
            </Typography>
            <Chip
              label={caseData.managementStatus === 'OPEN' ? 'Caso Abierto' : 'Caso Cerrado'}
              size="small"
              sx={{
                bgcolor:
                  caseData.managementStatus === 'OPEN'
                    ? alpha('#FF4C51', 0.1)
                    : alpha('#5DCC0A', 0.1),
                color: caseData.managementStatus === 'OPEN' ? '#FF4C51' : '#5DCC0A',
                fontWeight: 800,
                border: '1px solid',
                borderColor:
                  caseData.managementStatus === 'OPEN'
                    ? alpha('#FF4C51', 0.3)
                    : alpha('#5DCC0A', 0.3),
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Top Metrics Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ md: 4, sm: 6, xs: 12 }}>
          <Card
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #f0f0f0',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, mb: 1, color: '#2e263d', fontSize: '0.8rem' }}
            >
              Nivel de Riesgo
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <WarningIcon sx={{ color: caseData.risk?.color || '#FF4C51', fontSize: 24 }} />
              <Typography
                variant="h6"
                sx={{ color: caseData.risk?.color || '#FF4C51', fontWeight: 800 }}
              >
                {caseData.risk?.level}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Prioridad: {caseData.risk?.priority}
            </Typography>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #f0f0f0',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, mb: 1, color: '#2e263d', fontSize: '0.8rem' }}
            >
              Métrica NPS
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SadIcon
                sx={{
                  color:
                    caseData.nps?.classification === 'Promotor'
                      ? '#5DCC0A'
                      : caseData.nps?.classification === 'Neutro'
                        ? '#FFB400'
                        : '#FF4C51',
                  fontSize: 24,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  color:
                    caseData.nps?.classification === 'Promotor'
                      ? '#5DCC0A'
                      : caseData.nps?.classification === 'Neutro'
                        ? '#FFB400'
                        : '#FF4C51',
                  fontWeight: 800,
                }}
              >
                {caseData.nps?.classification} ({caseData.nps?.score})
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Puntaje: {caseData.nps?.score || 'N/A'}
            </Typography>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #f0f0f0',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, mb: 1, color: '#2e263d', fontSize: '0.8rem' }}
            >
              Tiempo de Resolución (SLA)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimeIcon
                  sx={{
                    color: caseData.sla?.resolutionExpired ? '#FF4C51' : '#5DCC0A',
                    fontSize: 24,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: caseData.sla?.resolutionExpired ? '#FF4C51' : '#5DCC0A',
                    fontWeight: 800,
                  }}
                >
                  {caseData.sla?.timeElapsedText}
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: caseData.sla?.resolutionExpired
                    ? alpha('#FF4C51', 0.05)
                    : alpha('#5DCC0A', 0.05),
                  p: 0.5,
                  px: 1,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: caseData.sla?.resolutionExpired
                    ? alpha('#FF4C51', 0.1)
                    : alpha('#5DCC0A', 0.1),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: caseData.sla?.resolutionExpired ? '#FF4C51' : '#5DCC0A',
                    fontWeight: 800,
                    display: 'block',
                  }}
                >
                  {caseData.sla?.resolutionExpired ? 'VENCIDO' : 'A TIEMPO'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                  Desde:{' '}
                  {caseData.sla?.scheduledAt
                    ? new Date(caseData.sla.scheduledAt).toLocaleString()
                    : 'N/A'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {caseData.sla?.firstResponseValue ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: alpha(
                      caseData.sla?.firstResponseExpired ? '#FF4C51' : '#5DCC0A',
                      0.05
                    ),
                    p: 0.5,
                    px: 1,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: alpha(
                      caseData.sla?.firstResponseExpired ? '#FF4C51' : '#5DCC0A',
                      0.1
                    ),
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    <strong>1ra respuesta:</strong> {caseData.sla.firstResponseValue}{' '}
                    {caseData.sla.firstResponseUnit}
                  </Typography>
                  <Chip
                    label={caseData.sla?.firstResponseExpired ? 'VENCIDO' : 'A TIEMPO'}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: '0.6rem',
                      bgcolor: 'transparent',
                      color: caseData.sla?.firstResponseExpired ? '#FF4C51' : '#5DCC0A',
                      fontWeight: 800,
                    }}
                  />
                </Box>
              ) : null}
              {caseData.sla?.resolutionValue ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: alpha(caseData.sla?.resolutionExpired ? '#FF4C51' : '#5DCC0A', 0.05),
                    p: 0.5,
                    px: 1,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: alpha(
                      caseData.sla?.resolutionExpired ? '#FF4C51' : '#5DCC0A',
                      0.1
                    ),
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    <strong>Cierre caso:</strong> {caseData.sla.resolutionValue}{' '}
                    {caseData.sla.resolutionUnit}
                  </Typography>
                  <Chip
                    label={caseData.sla?.resolutionExpired ? 'VENCIDO' : 'A TIEMPO'}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: '0.6rem',
                      bgcolor: 'transparent',
                      color: caseData.sla?.resolutionExpired ? '#FF4C51' : '#5DCC0A',
                      fontWeight: 800,
                    }}
                  />
                </Box>
              ) : null}
            </Box>
          </Card>
        </Grid>

        <Grid
          size={{ xs: 12, md: 2 }}
          sx={{ display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'center' }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, mb: 0.5, display: 'block', color: '#2e263d' }}
            >
              Estado del Caso
            </Typography>
            <Chip
              label={caseData.managementStatus === 'CLOSED' ? 'Caso Cerrado' : 'Caso Abierto'}
              sx={{
                fontWeight: 800,
                fontSize: '0.85rem',
                color: caseData.managementStatus === 'CLOSED' ? '#5DCC0A' : '#FF4C51',
                bgcolor:
                  caseData.managementStatus === 'CLOSED'
                    ? alpha('#5DCC0A', 0.1)
                    : alpha('#FF4C51', 0.1),
                border: '1px solid',
                borderColor:
                  caseData.managementStatus === 'CLOSED'
                    ? alpha('#5DCC0A', 0.3)
                    : alpha('#FF4C51', 0.3),
                borderRadius: 1.5,
                width: '100%',
                height: 38,
              }}
            />
          </Box>
          {caseData.managementStatus !== 'CLOSED' && (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => handleStatusChange('CLOSED')}
              startIcon={<CheckCircleIcon sx={{ fontSize: '1.2rem' }} />}
              sx={{
                bgcolor: '#4A148C',
                color: '#fff',
                '&:hover': { bgcolor: '#380b6b' },
                borderRadius: 1.5,
                py: 1,
                fontWeight: 700,
                textTransform: 'none',
              }}
            >
              Cerrar Caso
            </Button>
          )}
        </Grid>
      </Grid>

      {/* Main Content Columns (4 columns layout) */}
      <Grid container spacing={2}>
        {/* Column 1: Client & Survey Info */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Card
            sx={{
              p: 2,
              borderRadius: 2,
              mb: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #f0f0f0',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, mb: 2, color: '#2e263d', fontSize: '0.8rem' }}
            >
              Información del Cliente
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <PersonIcon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.2 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Nombre
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                  {caseData.client?.name || 'Anónimo'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <EmailIcon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.2 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Email
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, wordBreak: 'break-all', fontSize: '0.8rem' }}
                >
                  {caseData.client?.email || 'N/A'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <PhoneIcon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.2 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Teléfono
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                  {data?.clientPhone || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Card>

          <Card
            sx={{
              p: 2,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #f0f0f0',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, mb: 2, color: '#2e263d', fontSize: '0.8rem' }}
            >
              Información de la Encuesta
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <EventIcon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.2 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Fecha encuesta
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                  {caseData.surveyInfo?.date
                    ? new Date(caseData.surveyInfo.date).toLocaleString()
                    : 'N/A'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <Typography sx={{ color: 'text.secondary', fontSize: '1rem', fontWeight: 800 }}>
                ✓
              </Typography>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Evento / Servicio
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                  {caseData.surveyInfo?.event || 'N/A'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <DeviceIcon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.2 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Dispositivo
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                  {caseData.surveyInfo?.device || 'N/A'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <AgentIcon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.2 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Agente
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase' }}
                >
                  {caseData.surveyInfo?.agent || 'N/A'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <LinkIcon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.2 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Origen
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: '0.8rem', textTransform: 'capitalize' }}
                >
                  {caseData.surveyInfo?.origin || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Column 2: Low Rating Questions & New Follow-up */}
        <Grid size={{ xs: 12, md: 4 }}>
          {caseData.surveyInfo?.origin?.toLowerCase() === 'google' && data?.comments && (
            <Card
              sx={{
                p: 2,
                borderRadius: 2,
                mb: 2,
                bgcolor: alpha('#2e263d', 0.02),
                border: '1px dashed',
                borderColor: alpha('#2e263d', 0.2),
                boxShadow: 'none',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 800, mb: 2, color: '#2e263d', fontSize: '0.8rem' }}
              >
                Comentario de Google
              </Typography>
              <Box
                sx={{
                  bgcolor: 'white',
                  p: 1.5,
                  borderRadius: 2,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                  border: '1px solid',
                  borderColor: alpha('#2e263d', 0.1),
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontSize: '0.8rem', color: '#2e263d', fontStyle: 'italic' }}
                >
                  "{data.comments}"
                </Typography>
              </Box>
            </Card>
          )}
          <Card
            sx={{
              p: 2,
              borderRadius: 2,
              mb: 2,
              bgcolor: alpha('#FF4C51', 0.02),
              border: '1px dashed',
              borderColor: alpha('#FF4C51', 0.2),
              boxShadow: 'none',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, mb: 2, color: '#2e263d', fontSize: '0.8rem' }}
            >
              Preguntas con baja calificación
            </Typography>

            {caseData.lowScores &&
              caseData.lowScores.map((item: any, index: number) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    mb: 2,
                    bgcolor: 'white',
                    p: 1.5,
                    borderRadius: 2,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                    border: '1px solid',
                    borderColor: alpha('#FF4C51', 0.1),
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Box
                      sx={{
                        minWidth: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: '#FF4C51',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        fontSize: '0.7rem',
                        mt: 0.2,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#2e263d' }}
                    >
                      {item.questionText}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${item.score} / ${item.maxScore}`}
                    size="small"
                    sx={{
                      bgcolor: alpha('#FF4C51', 0.05),
                      color: '#FF4C51',
                      fontWeight: 800,
                      border: '1px solid #FF4C51',
                      borderRadius: 1,
                    }}
                  />
                </Box>
              ))}
          </Card>
        </Grid>

        {/* Column 3: History */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Card
            sx={{
              p: 2,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #f0f0f0',
              height: '100%',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, mb: 3, color: '#2e263d', fontSize: '0.8rem' }}
            >
              Historial de Seguimientos
            </Typography>

            <Box sx={{ position: 'relative', ml: 1, borderLeft: '1px solid #E0E0E0', pb: 1 }}>
              {caseData.followUps && caseData.followUps.length > 0 ? (
                caseData.followUps.map((item: any, index: number) => (
                  <Box key={index} sx={{ position: 'relative', mb: 3, pl: 2.5 }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        left: -4.5,
                        top: 5,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: '#5DCC0A',
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Box sx={{ bgcolor: alpha('#5DCC0A', 0.1), p: 0.4, borderRadius: 1 }}>
                        {item.contactMethod === 'whatsapp' ? (
                          <WhatsAppIcon sx={{ color: '#5DCC0A', fontSize: 14 }} />
                        ) : item.contactMethod === 'email' ? (
                          <MailIcon sx={{ color: '#5DCC0A', fontSize: 14 }} />
                        ) : (
                          <CallIcon sx={{ color: '#5DCC0A', fontSize: 14 }} />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'capitalize' }}
                      >
                        {item.contactMethod}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
                      >
                        por {item.agent}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        mb: 0.5,
                        fontSize: '0.7rem',
                      }}
                    >
                      {item.date ? new Date(item.date).toLocaleString() : ''}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#2e263d', fontSize: '0.75rem', lineHeight: 1.4 }}
                    >
                      {item.observations}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                  No hay seguimientos registrados.
                </Typography>
              )}
            </Box>

            {caseData.managementStatus !== 'CLOSED' && (
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #f0f0f0' }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 800, mb: 2, color: '#2e263d', fontSize: '0.8rem' }}
                >
                  Nuevo Seguimiento
                </Typography>

                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 12 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, mb: 0.5, display: 'block', color: 'text.secondary' }}
                    >
                      Medio de contacto
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={formFollowUp.contactMethod}
                        onChange={(e) =>
                          setFormFollowUp({
                            ...formFollowUp,
                            contactMethod: e.target.value as string,
                          })
                        }
                        sx={{ borderRadius: 1.5, fontSize: '0.8rem' }}
                      >
                        <MenuItem value="whatsapp" sx={{ fontSize: '0.8rem' }}>
                          WhatsApp
                        </MenuItem>
                        <MenuItem value="llamada" sx={{ fontSize: '0.8rem' }}>
                          Llamada
                        </MenuItem>
                        <MenuItem value="email" sx={{ fontSize: '0.8rem' }}>
                          Email
                        </MenuItem>
                        <MenuItem value="otro" sx={{ fontSize: '0.8rem' }}>
                          Otro
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, mb: 0.5, display: 'block', color: 'text.secondary' }}
                    >
                      Resultado del contacto
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={formFollowUp.result}
                        onChange={(e) =>
                          setFormFollowUp({ ...formFollowUp, result: e.target.value as string })
                        }
                        sx={{ borderRadius: 1.5, fontSize: '0.8rem' }}
                      >
                        <MenuItem value="Cliente respondió" sx={{ fontSize: '0.8rem' }}>
                          Cliente respondió
                        </MenuItem>
                        <MenuItem value="No respondió" sx={{ fontSize: '0.8rem' }}>
                          No respondió
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, mb: 0.5, display: 'block', color: 'text.secondary' }}
                    >
                      Observaciones
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      rows={2}
                      value={formFollowUp.observations}
                      onChange={(e) =>
                        setFormFollowUp({ ...formFollowUp, observations: e.target.value })
                      }
                      placeholder="Escribe aquí los detalles..."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8rem' } }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveFollowUp}
                    disabled={!formFollowUp.observations}
                    startIcon={<SaveIcon sx={{ fontSize: '1rem' }} />}
                    fullWidth
                    sx={{
                      bgcolor: '#4A148C',
                      '&:hover': { bgcolor: '#380b6b' },
                      borderRadius: 1.5,
                      py: 0.8,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontSize: '0.85rem',
                    }}
                  >
                    Guardar
                  </Button>
                </Box>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Column 4: SLA Summary */}
        <Grid size={{ xs: 12, md: 2 }}>
          <Card
            sx={{
              p: 2,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #f0f0f0',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                mb: 2,
                color: '#2e263d',
                fontSize: '0.8rem',
                textAlign: 'center',
              }}
            >
              Resumen SLA
            </Typography>

            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', mb: 2 }}
            >
              {/* 1ra Respuesta */}
              {caseData.sla?.firstResponseValue ? (
                <Box sx={{ textAlign: 'center', width: '100%' }}>
                  <Box
                    sx={{ position: 'relative', display: 'flex', justifyContent: 'center', mb: 1 }}
                  >
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={65}
                      thickness={5}
                      sx={{ color: '#f0f0f0' }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={Math.min(caseData.sla?.firstResponsePercentage || 0, 100)}
                      size={65}
                      thickness={5}
                      sx={{
                        color: caseData.sla?.firstResponseExpired
                          ? '#FF4C51'
                          : caseData.sla?.firstResponsePercentage > 80
                            ? '#FFB400'
                            : '#5DCC0A',
                        position: 'absolute',
                        left: 'auto',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#2e263d' }}>
                        {caseData.sla?.firstResponsePercentage || 0}%
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: 'text.secondary',
                      display: 'block',
                      lineHeight: 1.2,
                    }}
                  >
                    1ra Respuesta
                  </Typography>
                </Box>
              ) : null}

              {/* Cierre */}
              {caseData.sla?.resolutionValue ? (
                <Box sx={{ textAlign: 'center', width: '100%' }}>
                  <Box
                    sx={{ position: 'relative', display: 'flex', justifyContent: 'center', mb: 1 }}
                  >
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={65}
                      thickness={5}
                      sx={{ color: '#f0f0f0' }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={Math.min(caseData.sla?.resolutionPercentage || 0, 100)}
                      size={65}
                      thickness={5}
                      sx={{
                        color: caseData.sla?.resolutionExpired
                          ? '#FF4C51'
                          : caseData.sla?.resolutionPercentage > 80
                            ? '#FFB400'
                            : '#5DCC0A',
                        position: 'absolute',
                        left: 'auto',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#2e263d' }}>
                        {caseData.sla?.resolutionPercentage || 0}%
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: 'text.secondary',
                      display: 'block',
                      lineHeight: 1.2,
                    }}
                  >
                    Resolución
                  </Typography>
                </Box>
              ) : null}
            </Box>

            <Box sx={{ mb: 1, textAlign: 'center' }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mb: 0.2 }}
              >
                Tiempo transcurrido
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800, color: '#2e263d' }}>
                {caseData.sla?.timeElapsedText || 'N/A'}
              </Typography>
            </Box>

            {(caseData.sla?.firstResponseExpired || caseData.sla?.resolutionExpired) && (
              <Box
                sx={{
                  mt: 'auto',
                  bgcolor: alpha('#FF4C51', 0.05),
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: alpha('#FF4C51', 0.2),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#FF4C51',
                    fontWeight: 600,
                    textAlign: 'center',
                    display: 'block',
                    lineHeight: 1.3,
                  }}
                >
                  Existen tiempos vencidos en el SLA.
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
